/**
 * Seed map of high-value patient-intent conditions → related specialties.
 * Each condition slug maps to a display name, short blurb, and an ordered
 * list of specialty names that match entries in SPECIALTIES.
 *
 * Used by /conditions/$condition and /conditions/$condition/$citySlug.
 * Content is educational only, never diagnostic.
 */

export type SeoCondition = {
 slug: string;
 name: string;
 intro: string;
 specialties: string[]; // ordered by relevance
 relatedConditions?: string[];
};

export const SEO_CONDITIONS: SeoCondition[] = [
 {
 slug: "back-pain",
 name: "Back Pain",
 intro:
 "Back pain is one of the most common reasons people see a healthcare provider. Causes range from muscle strain to disc and joint conditions.",
 specialties: ["Chiropractors", "Physical Therapists", "Orthopedic Surgeons", "Pain Management", "Family Doctors"],
 relatedConditions: ["neck-pain", "sciatica"],
 },
 {
 slug: "neck-pain",
 name: "Neck Pain",
 intro: "Neck pain can stem from posture, injury, or underlying joint and disc issues.",
 specialties: ["Chiropractors", "Physical Therapists", "Orthopedic Surgeons", "Pain Management"],
 relatedConditions: ["back-pain"],
 },
 {
 slug: "sciatica",
 name: "Sciatica",
 intro: "Sciatica refers to pain radiating along the sciatic nerve from the lower back down the leg.",
 specialties: ["Orthopedic Surgeons", "Neurologists", "Pain Management", "Physical Therapists", "Chiropractors"],
 },
 {
 slug: "anxiety",
 name: "Anxiety",
 intro: "Anxiety is a treatable condition. Therapy and, when needed, medication management can both help.",
 specialties: ["Therapists", "Psychologists", "Psychiatrists", "Family Doctors"],
 relatedConditions: ["depression", "panic-attacks"],
 },
 {
 slug: "depression",
 name: "Depression",
 intro: "Depression is common and treatable with therapy, medication, or both.",
 specialties: ["Therapists", "Psychologists", "Psychiatrists", "Family Doctors"],
 relatedConditions: ["anxiety"],
 },
 {
 slug: "panic-attacks",
 name: "Panic Attacks",
 intro: "Panic attacks are sudden episodes of intense fear. They are treatable with therapy and sometimes medication.",
 specialties: ["Therapists", "Psychologists", "Psychiatrists"],
 },
 {
 slug: "adhd",
 name: "ADHD",
 intro: "ADHD diagnosis and care typically involves evaluation and ongoing treatment with a qualified clinician.",
 specialties: ["Psychiatrists", "Psychologists", "Family Doctors", "Pediatricians"],
 },
 {
 slug: "tooth-pain",
 name: "Tooth Pain",
 intro: "Tooth pain can indicate decay, infection, or other dental issues that need prompt evaluation.",
 specialties: ["Dentists", "Endodontists", "Oral Surgeons"],
 relatedConditions: ["gum-disease"],
 },
 {
 slug: "gum-disease",
 name: "Gum Disease",
 intro: "Gum disease is common and progressive. Early treatment helps prevent tooth loss.",
 specialties: ["Dentists", "Periodontists"],
 },
 {
 slug: "acne",
 name: "Acne",
 intro: "Acne can affect people of any age. Treatment options range from topical care to prescription medication.",
 specialties: ["Dermatologists", "Family Doctors"],
 },
 {
 slug: "eczema",
 name: "Eczema",
 intro: "Eczema is a chronic skin condition that can be managed with targeted skincare and prescription treatments.",
 specialties: ["Dermatologists", "Allergists / Immunologists"],
 },
 {
 slug: "allergies",
 name: "Allergies",
 intro: "Allergies can be seasonal, environmental, or food-related. Testing and treatment can reduce symptoms.",
 specialties: ["Allergists / Immunologists", "ENT (Ear, Nose & Throat)", "Family Doctors"],
 },
 {
 slug: "asthma",
 name: "Asthma",
 intro: "Asthma is a manageable condition. Ongoing care helps reduce flare-ups and emergency visits.",
 specialties: ["Allergists / Immunologists", "Family Doctors", "Pediatricians"],
 },
 {
 slug: "diabetes",
 name: "Diabetes",
 intro: "Diabetes care benefits from a coordinated team that monitors blood sugar and prevents complications.",
 specialties: ["Endocrinologists", "Family Doctors", "Dietitians / Nutritionists"],
 },
 {
 slug: "high-blood-pressure",
 name: "High Blood Pressure",
 intro: "High blood pressure is a leading cause of serious health problems and is treatable with lifestyle and medication.",
 specialties: ["Cardiologists", "Family Doctors"],
 },
 {
 slug: "high-cholesterol",
 name: "High Cholesterol",
 intro: "High cholesterol often has no symptoms but can be managed through lifestyle and medication.",
 specialties: ["Cardiologists", "Family Doctors"],
 },
 {
 slug: "migraine",
 name: "Migraine",
 intro: "Migraines are recurrent and treatable. A specialist can help identify triggers and tailor a plan.",
 specialties: ["Neurologists", "Family Doctors"],
 },
 {
 slug: "knee-pain",
 name: "Knee Pain",
 intro: "Knee pain can stem from injury, arthritis, or overuse. Imaging and a tailored plan often help.",
 specialties: ["Orthopedic Surgeons", "Physical Therapists", "Pain Management"],
 },
 {
 slug: "shoulder-pain",
 name: "Shoulder Pain",
 intro: "Shoulder pain commonly results from rotator cuff issues, arthritis, or injury.",
 specialties: ["Orthopedic Surgeons", "Physical Therapists"],
 },
 {
 slug: "hip-pain",
 name: "Hip Pain",
 intro: "Hip pain can be caused by arthritis, bursitis, or injury. Targeted treatment is effective.",
 specialties: ["Orthopedic Surgeons", "Physical Therapists"],
 },
 {
 slug: "infertility",
 name: "Infertility",
 intro: "Infertility care begins with evaluation. Many couples successfully conceive with appropriate treatment.",
 specialties: ["Gynecologists"],
 },
 {
 slug: "pregnancy",
 name: "Pregnancy Care",
 intro: "Routine prenatal care supports a healthy pregnancy from early on.",
 specialties: ["Gynecologists", "Family Doctors"],
 },
 {
 slug: "ear-infection",
 name: "Ear Infection",
 intro: "Ear infections are common and usually treatable with timely evaluation.",
 specialties: ["ENT (Ear, Nose & Throat)", "Pediatricians", "Family Doctors"],
 },
 {
 slug: "vision-problems",
 name: "Vision Problems",
 intro: "Vision changes warrant a comprehensive eye exam.",
 specialties: ["Eye Doctors / Optometrists"],
 },
 {
 slug: "hearing-loss",
 name: "Hearing Loss",
 intro: "Hearing loss is common and often treatable with evaluation and devices.",
 specialties: ["Audiologists", "ENT (Ear, Nose & Throat)"],
 },
 {
 slug: "weight-loss",
 name: "Weight Loss",
 intro: "Sustainable weight loss benefits from medical and nutritional guidance.",
 specialties: ["Dietitians / Nutritionists", "Endocrinologists", "Family Doctors"],
 },
 {
 slug: "thyroid-disorders",
 name: "Thyroid Disorders",
 intro: "Thyroid disorders are common and very treatable once diagnosed.",
 specialties: ["Endocrinologists", "Family Doctors"],
 },
 {
 slug: "menopause",
 name: "Menopause",
 intro: "Menopause symptoms can be effectively managed with several treatment options.",
 specialties: ["Gynecologists", "Family Doctors"],
 },
 {
 slug: "snoring-sleep-apnea",
 name: "Snoring & Sleep Apnea",
 intro: "Loud snoring and sleep apnea benefit from evaluation and treatment to improve sleep and heart health.",
 specialties: ["ENT (Ear, Nose & Throat)", "Family Doctors"],
 },
 {
 slug: "digestive-issues",
 name: "Digestive Issues",
 intro: "Persistent digestive issues should be evaluated by a specialist.",
 specialties: ["Gastroenterologists", "Family Doctors"],
 },
];

export function conditionFromSlug(slug: string): SeoCondition | undefined {
 const s = slug.toLowerCase();
 return SEO_CONDITIONS.find((c) => c.slug === s);
}
